import { supabase } from '@/lib/supabase';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * SENSITIVE-DATA ACCESS LOGGING (client side)
 * ───────────────────────────────────────────────────────────────────────────
 * Tells the server "this viewer just saw the contact fields on this listing".
 * It sends NOTHING private: a listing id and a list of FIELD NAMES.
 *
 * Everything that decides whether the row is trustworthy is worked out on the
 * server from the session token — viewer id, role, admin flag, whether a
 * purchase exists. This file cannot claim any of it, and the log is read-only
 * to admins, so a tampered call can only ever fail.
 *
 * SAFETY RULES
 *  1. Never blocks a render. It is fire-and-forget and every failure is
 *     swallowed; a page must not freeze because an audit row did not write.
 *  2. Never fires twice for the same view. React Strict Mode runs effects
 *     twice in development and a parent re-render can remount a panel, so an
 *     in-memory guard drops repeats, and the server collapses anything that
 *     slips through with a unique dedupe key.
 *  3. Only runs for a signed-in session. Logged-out visitors never see
 *     contact data, so there is nothing to record.
 */

export type PiiSurface = 'admin_project_dossier' | 'purchased_contact_card' | 'admin_contact_reveal';

/** Field identifiers only. Values are never sent. */
export type PiiField = 'contact_name' | 'phone' | 'email' | 'address' | 'documents' | 'notes';

/** Views already reported this page-life, so re-renders stay silent. */
const seen = new Set<string>();

export const resetPiiAccessGuard = () => seen.clear();

export const logPiiAccess = async (opts: {
  listingId?: string | null;
  surface: PiiSurface;
  fields?: PiiField[];
  language?: string;
}): Promise<{ logged: boolean; deduped?: boolean; error?: string }> => {
  const listingId = String(opts.listingId || '').trim();
  if (!listingId) return { logged: false, error: 'no_listing' };

  const guardKey = `${opts.surface}:${listingId}`;
  if (seen.has(guardKey)) return { logged: false, deduped: true };
  seen.add(guardKey);

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) return { logged: false, error: 'no_session' };

    const { data, error } = await supabase.functions.invoke('pii-access-log', {
      body: {
        listingId,
        surface: opts.surface,
        fields: opts.fields || [],
        language: opts.language || 'en',
      },
    });
    if (error) return { logged: false, error: error.message };
    return {
      logged: (data as { logged?: boolean } | null)?.logged === true,
      deduped: (data as { deduped?: boolean } | null)?.deduped === true,
    };
  } catch (e) {
    // Audit failures are invisible to the customer by design.
    return { logged: false, error: (e as Error)?.message || 'unknown' };
  }
};

/** Field list for the unlocked contact card a purchaser sees. */
export const CONTACT_CARD_FIELDS: PiiField[] = ['contact_name', 'phone', 'email', 'address'];

/** Field list for the full admin dossier. */
export const DOSSIER_FIELDS: PiiField[] = ['contact_name', 'phone', 'email', 'address', 'documents', 'notes'];
